import { AccountInfo, Configuration, InteractionRequiredAuthError, IPublicClientApplication, PublicClientApplication } from "@azure/msal-browser";
import { fetchMsalConfig } from "./api";

// Singleton MSAL instance
let msalInstance: IPublicClientApplication;
let account: AccountInfo | null = null;

/**
 * Initializes MSAL instance and handles redirect promise.
 * Ensures proper setup before authentication flows.
 */
export async function initializeMsal(): Promise<IPublicClientApplication> {
    if (!msalInstance) {
        const msalConfig: Configuration = await fetchMsalConfig();
        msalInstance = new PublicClientApplication(msalConfig);
        await msalInstance.initialize();

        try {
            await msalInstance.handleRedirectPromise(); // Ensure redirect flows are completed
        } catch (error) {
            console.error("Error handling redirect promise:", error);
        }

        const accounts = msalInstance.getAllAccounts();
        account = accounts.length > 0 ? accounts[0] : null;
    }
    return msalInstance;
}

/**
 * Retrieves the currently signed-in account.
 */
export function getAccount(): AccountInfo | null {
    return account;
}

/**
 * Retrieves the scope for accessing the API.
 */
function getApiScope(): string {
    if (!msalInstance) {
        throw new Error("MSAL instance is not initialized");
    }

    const { auth } = msalInstance.getConfiguration();
    return `api://${auth.clientId}/access_as_user`;
}

/**
 * Attempts to acquire an access token silently.
 * Falls back to interactive authentication if required.
 */
export async function acquireToken(): Promise<string> {
    if (!msalInstance) {
        throw new Error("MSAL instance is not initialized");
    }
    if (!account) {
        throw new Error("No account is signed in");
    }

    const scope = getApiScope();

    try {
        const response = await msalInstance.acquireTokenSilent({
            account,
            scopes: [scope]
        });
        return response.accessToken;
    } catch (error) {
        if (error instanceof InteractionRequiredAuthError) {
            console.warn("Silent token acquisition failed, requiring user interaction", error);
            return await acquireTokenInteractive();
        } else {
            console.error("Unexpected token acquisition error:", error);
            throw error;
        }
    }
}

/**
 * Performs interactive token acquisition via popup.
 */
async function acquireTokenInteractive(): Promise<string> {
    if (!msalInstance) {
        throw new Error("MSAL instance is not initialized");
    }

    const scope = getApiScope();

    try {
        const response = await msalInstance.acquireTokenPopup({
            scopes: [scope]
        });
        return response.accessToken;
    } catch (error) {
        console.error("Interactive token acquisition failed", error);
        throw error;
    }
}

/**
 * Logs in the user via popup authentication.
 * Updates the global `account` variable upon success.
 */
export async function login(): Promise<void> {
    if (!msalInstance) {
        throw new Error("MSAL instance is not initialized");
    }

    try {
        const response = await msalInstance.loginPopup({
            scopes: [".default"]
        });
        account = response.account;
        console.info("User successfully logged in:", account.username);
    } catch (error) {
        console.error("Login failed:", error);
        throw error;
    }
}

/**
 * Logs out the current user via popup.
 * Clears the stored account information.
 */
export async function logout(): Promise<void> {
    if (!msalInstance) {
        throw new Error("MSAL instance is not initialized");
    }

    try {
        const logoutRequest = { account };
        await msalInstance.logoutPopup(logoutRequest);
        account = null;
        console.info("User successfully logged out");
    } catch (error) {
        console.error("Logout failed:", error);
    }
}
